import type { ReactNode } from 'react'
import type { ModuleName } from '@/constants/modules'
import type { Action, Resource } from '@/constants/permissions'
import { useManifest } from '@/hooks/useManifest'
import { usePermission } from '@/hooks/usePermission'
import { ForbiddenPage } from '@/pages/system/ForbiddenPage'
import { NotFoundPage } from '@/pages/system/NotFoundPage'
import { useAuthStore } from '@/store/auth.store'

export interface ModuleRouteProps {
  module: ModuleName
  children: ReactNode
}

/** Renders children only if the module is active in the manifest; else 404. */
export function ModuleRoute({ module, children }: ModuleRouteProps) {
  const { isModuleActive } = useManifest()
  if (!isModuleActive(module)) return <NotFoundPage />
  return <>{children}</>
}

export interface RoleRouteProps {
  roles: readonly string[]
  children: ReactNode
}

/** Renders children only if the user's role is in the allowed set; else 403. */
export function RoleRoute({ roles, children }: RoleRouteProps) {
  const role = useAuthStore((s) => s.user?.role)
  if (!role || !roles.includes(role)) return <ForbiddenPage />
  return <>{children}</>
}

export interface PermissionRouteProps {
  resource: Resource
  action: Action
  children: ReactNode
}

/**
 * Renders children only if the user holds `${resource}:${action}`; else 403.
 *
 * Used for the authoring surfaces (Stage 2.5 §5): viewing a dashboard and
 * authoring the connection behind it are different grants. The server enforces
 * the same split — this guard hides the screen, it does not grant anything.
 */
export function PermissionRoute({ resource, action, children }: PermissionRouteProps) {
  const allowed = usePermission(resource, action)
  if (!allowed) return <ForbiddenPage />
  return <>{children}</>
}
